import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardApi, type DashboardData } from '../services/api';

export function Dashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    dashboardApi.get()
      .then(setData)
      .catch((err) => setError(err.detail || 'Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="loading-page"><div className="spinner" /></div>;
  }

  if (error) {
    return (
      <div className="page-body">
        <div className="card" style={{ textAlign: 'center', padding: 48 }}>
          <h3 style={{ color: 'var(--status-fail)', marginBottom: 8 }}>Error Loading Dashboard</h3>
          <p className="text-muted">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const overview = data.overview || {
    totalLines: 0, activeLines: 0, totalInspections: 0,
    totalFails: 0, totalPass: 0, overallPassRate: 0,
  };

  return (
    <>
      <div className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1>Dashboard</h1>
            <p>Real-time seal quality overview across all production lines</p>
          </div>
          {!data.hasAccess && (
            <div className="badge badge-warning" style={{ padding: '6px 12px', fontSize: '0.85rem' }}>
              ⚠ No Active Subscription
            </div>
          )}
        </div>
      </div>

      <div className="page-body">
        {/* Stats Overview */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Total Inspections</div>
            <div className="stat-value">{overview.totalInspections.toLocaleString()}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Pass Rate</div>
            <div className={`stat-value ${overview.overallPassRate >= 99 ? 'pass' : overview.overallPassRate >= 95 ? 'warning' : 'fail'}`}>
              {overview.overallPassRate.toFixed(1)}%
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Failed Inspections</div>
            <div className="stat-value fail">{overview.totalFails.toLocaleString()}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Active Lines</div>
            <div className="stat-value info">
              {overview.activeLines} / {overview.totalLines}
            </div>
          </div>
        </div>

        {/* Production Lines */}
        <div className="card mb-6">
          <div className="card-header">
            <div>
              <div className="card-title">Production Lines</div>
              <div className="card-subtitle">Status and yield for each line</div>
            </div>
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/lines')}>
              Manage Lines
            </button>
          </div>

          {data.lines && data.lines.length > 0 ? (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Line</th>
                    <th>Status</th>
                    <th>Inspections</th>
                    <th>Pass Rate</th>
                    <th>Failures</th>
                    <th>Devices</th>
                    <th>Last Activity</th>
                  </tr>
                </thead>
                <tbody>
                  {data.lines.map((line) => (
                    <tr key={line.id} onClick={() => navigate(`/lines/${line.id}`)} style={{ cursor: 'pointer' }}>
                      <td>
                        <div style={{ fontWeight: 500 }}>{line.name}</div>
                        <div className="text-sm text-muted font-mono">{line.code}</div>
                      </td>
                      <td>
                        <span className={`badge ${
                          line.status === 'active' ? 'badge-pass' :
                          line.status === 'paused' ? 'badge-warning' :
                          line.status === 'maintenance' ? 'badge-info' : 'badge-neutral'
                        }`}>
                          {line.status}
                        </span>
                      </td>
                      <td className="font-mono">{(line.stats?.totalInspections || 0).toLocaleString()}</td>
                      <td>
                        <span className={`font-mono ${
                          (line.stats?.passRate || 0) >= 99 ? 'pass' :
                          (line.stats?.passRate || 0) >= 95 ? 'warning' : 'fail'
                        }`} style={{ color: 'inherit' }}>
                          {(line.stats?.passRate || 0).toFixed(1)}%
                        </span>
                      </td>
                      <td className="font-mono" style={{ color: 'var(--status-fail)' }}>
                        {line.stats?.failCount || 0}
                      </td>
                      <td>
                        {line.devices?.length ? (
                          <span className="flex items-center gap-2">
                            <span className={`status-dot ${line.devices.some(d => d.status === 'online') ? 'online' : 'offline'}`} />
                            {line.devices.filter(d => d.status === 'online').length}/{line.devices.length}
                          </span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="text-sm text-muted">
                        {line.stats?.lastInspectionAt
                          ? new Date(line.stats.lastInspectionAt).toLocaleString()
                          : 'No data'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 20h20" /><path d="M5 20V8l7-5 7 5v12" /><path d="M9 20v-6h6v6" />
              </svg>
              <h3>No Production Lines Yet</h3>
              <p>Set up your first production line to start monitoring seal quality.</p>
              <button className="btn btn-primary" onClick={() => navigate('/lines')}>
                Add Production Line
              </button>
            </div>
          )}
        </div>

        {/* Recent Failures */}
        {data.recentFailures && data.recentFailures.length > 0 && (
          <div className="card">
            <div className="card-header">
              <div>
                <div className="card-title">Recent Failures</div>
                <div className="card-subtitle">Latest rejected packages requiring attention</div>
              </div>
              <button className="btn btn-secondary btn-sm" onClick={() => navigate('/inspections')}>
                View All
              </button>
            </div>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Line</th>
                    <th>Result</th>
                    <th>Defect Type</th>
                    <th>Confidence</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recentFailures.map((fail: any) => (
                    <tr key={fail.id}>
                      <td className="text-sm font-mono">{new Date(fail.inspectedAt).toLocaleString()}</td>
                      <td>{fail.lineName || fail.lineCode}</td>
                      <td><span className="badge badge-fail">FAIL</span></td>
                      <td>{fail.defectType ? formatDefectType(fail.defectType) : '—'}</td>
                      <td className="font-mono">
                        {fail.confidenceScore ? `${(fail.confidenceScore * 100).toFixed(0)}%` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function formatDefectType(type: string): string {
  const labels: Record<string, string> = {
    contamination: 'Contamination',
    wrinkle: 'Wrinkle / Fold',
    under_heating: 'Under-Heating',
    over_heating: 'Over-Heating',
    incomplete_seal: 'Incomplete Seal',
    misalignment: 'Misalignment',
    other: 'Other',
  };
  return labels[type] || type;
}
